<template>
  <div class="report-analytics-container">
    <template v-if="!clips || clips.length === 0">
      <div class="empty-container">
        <i class="far fa-chart-bar chart-icon"></i>
        <h4>{{$t('report.empty')}}</h4>
      </div>
    </template>
    <template v-else>
      <div class="analytic-card">
        <h5 class="card-text">Overall Duration</h5>
        <apexchart
          height="200"
          type="donut"
          :options="donut.chartOptions"
          :series="donut.series">
        </apexchart>
      </div>
      <div class="analytic-card">
        <h5 class="card-text">Clip Count By Day of Week</h5>
        <apexchart
          height="200"
          type="bar"
          :options="bar.chartOptions"
          :series="bar.series" >
        </apexchart>
      </div>
      <div class="analytic-card analytic-card--table">
        <h5 class="card-text">Clips Created By User</h5>
        <md-table>
          <md-table-row>
            <md-table-head>User</md-table-head>
            <md-table-head>Count</md-table-head>
          </md-table-row>
          <md-table-row
            v-for="(user, index) in createdByUser"
            :key="index">
            <md-table-cell>{{user.user}}</md-table-cell>
            <md-table-cell>{{user.count}}</md-table-cell>
          </md-table-row>
        </md-table>
      </div>
      <div class="analytic-card-large">
        <h5 class="card-text">Duration By Day (minutes)</h5>
        <apexchart
          height="300"
          type="bar"
          :options="stackedBar.chartOptions"
          :series="stackedBar.series" >
        </apexchart>
      </div>
      <div class="analytic-card-large">
        <h5 class="card-text">Clip Count By Day</h5>
        <apexchart
          height="300"
          type="bar"
          :options="stackedBar2.chartOptions"
          :series="stackedBar2.series" >
        </apexchart>
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';
import {
  getDaysInMonth,
  getDay,
  getDate,
  parseISO,
} from 'date-fns';

import {
  getDurationChartOptions,
  getBarChartOptions,
  getDaysInMonthBarChartOptions,
} from '@/components/parts/report/chartOption';

export default {
  props: ['clips', 'selectedMonth', 'selectedYear', 'onSetClipsFilter'],
  data() {
    return {
      donut: {},
      bar: {},
      stackedBar: {},
      stackedBar2: [],
    };
  },
  watch: {
    clips() {
      this.updateAllCharts();
    },
  },
  computed: {
    daysInMonth() {
      const days = getDaysInMonth(new Date(this.selectedYear, this.selectedMonth));
      return _.times(days);
    },
    durations() {
      return _.reduce(this.clips, (result, report) => {
        const newResult = result;
        const minute = 60;
        if (report.duration < 1 * minute) newResult.mini += 1;
        else if (report.duration < 5 * minute) newResult.short += 1;
        else if (report.duration < 15 * minute) newResult.average += 1;
        else if (report.duration < 30 * minute) newResult.long += 1;
        else newResult.massive += 1;
        return newResult;
      }, {
        mini: 0,
        short: 0,
        average: 0,
        long: 0,
        massive: 0,
      });
    },
    dayInWeekCreated() {
      const dayInWeeks = _.reduce(this.clips, (result, r) => {
        const newResult = result;
        const day = getDay(parseISO(r.startedAt));
        newResult[day] += 1;
        return newResult;
      }, {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        6: 0,
        7: 0,
      });
      return dayInWeeks;
    },
    durationByDay() {
      // NOTE: Change this to real month
      return _.reduce(this.clips, (result, r) => {
        const newResult = result;
        const day = getDate(parseISO(r.startedAt));
        if (!newResult[day + 1]) newResult[day + 1] = Math.ceil(r.duration / 60);
        else newResult[day + 1] += Math.ceil(r.duration / 60);
        return newResult;
      }, {});
    },
    amountByDay() {
      // NOTE: Change this to real month
      return _.reduce(this.clips, (result, r) => {
        const newResult = result;
        const day = getDate(parseISO(r.startedAt));
        if (!newResult[day + 1]) newResult[day + 1] = 1;
        else newResult[day + 1] += 1;
        return newResult;
      }, {});
    },
    createdByUser() {
      return _.map(_.groupBy(this.clips, 'creatorUsername'), (v, key) => {
        const user = this.substringTeamName(key);
        const obj = {
          user,
          count: v.length,
        };
        return obj;
      });
    },
  },
  created() {
    this.updateDurationChart();
    this.updateBar();
    this.updateStackedBar();
    this.updateStackedBar2();
  },
  methods: {
    async updateAllCharts() {
      await this.updateDurationChart();
      await this.updateBar();
      await this.updateStackedBar();
      await this.updateStackedBar2();
    },
    onClickBarChart(event, chartContext, config) {
      this.onSetClipsFilter(config.dataPointIndex + 1);
    },
    updateDurationChart() {
      this.donut = {
        chartOptions: getDurationChartOptions(),
        series: _.map(this.durations, d => d),
      };
    },
    updateBar() {
      this.bar = {
        chartOptions: getBarChartOptions(this.onClickBarChart),
        series: [
          {
            name: 'clips',
            data: _.map(this.dayInWeekCreated, r => r),
          },
        ],
      };
    },
    updateStackedBar() {
      this.stackedBar = {
        chartOptions: getDaysInMonthBarChartOptions(this.daysInMonth, this.onClickBarChart),
        series: [
          {
            name: 'duration',
            data: _.map(this.daysInMonth, (value) => {
              if (!this.durationByDay[value + 1]) return 0;
              return this.durationByDay[value + 1];
            }),
          },
        ],
      };
    },
    updateStackedBar2() {
      this.stackedBar2 = {
        chartOptions: getDaysInMonthBarChartOptions(this.daysInMonth, this.onClickBarChart),
        series: [
          {
            name: 'amount',
            data: _.map(this.daysInMonth, (value) => {
              if (!this.amountByDay[value + 1]) return 0;
              return this.amountByDay[value + 1];
            }),
          },
        ],
      };
    },
    substringTeamName(fullName) {
      return fullName.split('/')[1];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';

.empty-container {
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  .chart-icon {
    font-size: 72px;
    margin-bottom: $spacer;
  }
}

.report-analytics-container {
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  justify-content: space-between;
  .analytic-card {
    flex: 0 0 32.5%;
    background-color: $dark-secondary;
    box-shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
    height: 250px;
    margin-bottom: $spacer;
    padding: 20px 10px;
    &.analytic-card--table {
      overflow-x: hidden;
      overflow-y: auto;
      .card-text {
        margin-bottom: 10px;
      }
      ::v-deep .md-table-head-container {
        height: 32px;
        padding: 10px 0;
      }
      ::v-deep .md-table-head-label {
        line-height: 1;
      }
    }
  }
  .analytic-card-large {
    flex: 0 0 100%;
    background-color: $dark-secondary;
    box-shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
    height: auto;
    margin-bottom: $spacer;
    padding: 20px 10px;
  }
  .card-text {
    text-align: center;
    color: #FFF;
  }
}
</style>
